import * as React from 'react';

import { useMeasureBySelector } from '@hooks/useMeasure';

type Options = {
  wrapSelector?: string;
  hideOffsetPx?: number;
};

type Result<T extends HTMLElement> = {
  ref: React.RefObject<T>;
  isVisible: boolean;
};

export const useStickyBarWithinLayoutMainWrap = <
  T extends HTMLElement = HTMLDivElement
>({
  wrapSelector = '#layout-main-wrap',
  hideOffsetPx = 12,
}: Options = {}): Result<T> => {
  const ref = React.useRef<T>(null);
  const elementHeightRef = React.useRef(0);
  const rafIdRef = React.useRef<number | null>(null);
  const [isVisible, setIsVisible] = React.useState(true);

  const update = React.useCallback(() => {
    const wrapEl = document.querySelector<HTMLElement>(wrapSelector);
    if (!wrapEl) {
      setIsVisible(true);
      return;
    }

    const el = ref.current;
    if (el) {
      elementHeightRef.current = el.getBoundingClientRect().height;
    }

    const stickyHeight = elementHeightRef.current || 0;
    const wrapRect = wrapEl.getBoundingClientRect();

    // Sticky bar occupies viewport y=[0..stickyHeight].
    // Keep it visible only while this top slice overlaps the layout wrap.
    const overlapsTopViewportSlice =
      wrapRect.top < stickyHeight &&
      wrapRect.bottom > stickyHeight + hideOffsetPx;

    setIsVisible(overlapsTopViewportSlice);
  }, [hideOffsetPx, wrapSelector]);

  const scheduleUpdate = React.useCallback(() => {
    if (rafIdRef.current !== null) {
      return;
    }

    rafIdRef.current = window.requestAnimationFrame(() => {
      rafIdRef.current = null;
      update();
    });
  }, [update]);

  useMeasureBySelector(wrapSelector, scheduleUpdate);

  React.useEffect(() => {
    scheduleUpdate();

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);

      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [scheduleUpdate]);

  return {
    ref,
    isVisible,
  };
};
